import React, { createContext, FC, ReactNode, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@descope/react-sdk';

import { OfficeDTO, useGetOffices } from '@/api/generated';

const OfficeContext = createContext<{
  offices: OfficeDTO[] | undefined;
  refreshOffices: () => void;
  officeMap: Map<string, OfficeDTO>;
  officesOptions: { id: string; label: string }[];
}>({
  offices: undefined,
  refreshOffices: () => {},
  officeMap: new Map(),
  officesOptions: []
});

interface OfficeProviderProps {
  children: ReactNode;
}

export const OfficeProvider: FC<OfficeProviderProps> = ({ children }) => {
  const { isAuthenticated } = useSession();
  const { t, i18n } = useTranslation('filters');
  const { data, refetch: refreshOffices } = useGetOffices(undefined, { query: { enabled: isAuthenticated } });

  const offices = useMemo(
    () =>
      data?.map(
        (office: OfficeDTO) =>
          ({
            ...office,
            name: (office.name || '').replace('Office', '').trim()
          }) as OfficeDTO
      ),
    [data]
  );
  const contextValue = useMemo(
    () => ({
      offices,
      refreshOffices,
      officeMap: new Map(offices?.map(office => [office.slug!, office])),
      officesOptions: (offices ?? []).map(({ slug, key }) => ({
        id: slug!,
        label: t(`filters.options.office.${key!}`)
      }))
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recompute when locale changes
    [offices, refreshOffices, t, i18n.language]
  );

  return <OfficeContext.Provider value={contextValue}>{children}</OfficeContext.Provider>;
};

export const useOffices = () => useContext(OfficeContext);
